import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'

import { Factor } from './tabs/Factor/Factor'
import { Introduction } from './tabs/Introduction/Introduction'
import { PersonalityFactors } from './tabs/PersonalityFactors/PersonalityFactors'
import { Competency } from './tabs/Competency/Competency'
import { ProcessingSpeed } from './tabs/ProcessingSpeed/ProcessingSpeed'

const AGREEABLENESS = 'agreeableness'
const CONSCIENTIOUSNESS = 'conscientiousness'
const COMPETENCY = 'competency'
const EXTRAVERSION = 'extraversion'
const INTRODUCTION = 'introduction'
const OVERVIEW = 'overview'
const PERSONALITY = 'personality'
const GMA = 'gma'
const OPENNESS = 'openness'
const STABILITY = 'stability'
const HARDINESS = 'hardiness'

export const TAB_NAMES = {
  INTRODUCTION,
  GMA,
  OVERVIEW,
  COMPETENCY,
  PERSONALITY,
  AGREEABLENESS,
  CONSCIENTIOUSNESS,
  EXTRAVERSION,
  OPENNESS,
  STABILITY,
  HARDINESS,
}

export const getTabs = (noCompetencies, noGMA, noPersonality, candidates) => [
  ...(candidates.length > 1
    ? []
    : [{ id: TAB_NAMES.INTRODUCTION, label: t`Overview` }]),
  ...(noCompetencies
    ? []
    : [{ id: TAB_NAMES.COMPETENCY, label: t`Competency` }]),
  ...(noGMA ? [] : [{ id: TAB_NAMES.GMA, label: t`GMA` }]),
  ...(noPersonality
    ? []
    : [{ id: TAB_NAMES.PERSONALITY, label: t`Personality` }]),
]

export const personalityOverviewTab = () => ({
  id: TAB_NAMES.OVERVIEW,
  label: t`Overview`,
})

export const getFactors = isCandidate => [
  {
    id: TAB_NAMES.GMA,
    parentId: TAB_NAMES.GMA,
    label: t`GMA`,
    factor: 'GMA',
  },
  {
    id: TAB_NAMES.AGREEABLENESS,
    parentId: TAB_NAMES.PERSONALITY,
    label: t`Agreeableness`,
    factor: 'Agreeableness',
  },
  {
    id: TAB_NAMES.CONSCIENTIOUSNESS,
    parentId: TAB_NAMES.PERSONALITY,
    label: t`Conscientiousness`,
    factor: 'Conscientiousness',
  },
  {
    id: TAB_NAMES.EXTRAVERSION,
    parentId: TAB_NAMES.PERSONALITY,
    label: t`Extraversion`,
    factor: 'Extraversion',
  },
  {
    id: TAB_NAMES.OPENNESS,
    parentId: TAB_NAMES.PERSONALITY,
    label: t`Openness to Experience`,
    factor: 'Openness to Experience',
    groups: [
      {
        label: t`External Openness`,
        explanation: t`describes a person's interest in variation, new experiences, and exploration.`,
        traits: ['O4', 'O5', 'O6'],
      },
      {
        label: t`Internal Openness`,
        explanation: t`describes a person's curiosity for ideas, internal reflection, and abstract thinking.`,
        traits: ['O1', 'O2', 'O3'],
      },
    ],
  },
  {
    id: TAB_NAMES.STABILITY,
    parentId: TAB_NAMES.PERSONALITY,
    label: t`Emotional stability`,
    factor: 'Emotional Stability',
  },
  ...(isCandidate
    ? []
    : [
        {
          id: TAB_NAMES.HARDINESS,
          parentId: TAB_NAMES.PERSONALITY,
          label: t`Hardiness`,
          factor: 'Hardiness',
        },
      ]),
]

export const getActiveTab = (state, handlers) => {
  const {
    language,
    response,
    tab,
    personalityTab,
    project,
    selectedCompetency,
    selectedCandidates,
    candidates,
  } = state
  const {
    onTab,
    setPersonalityTab,
    setSelectedCompetency,
    setSelectedCandidates,
  } = handlers

  const scores = response?.map(resp => resp.scores) || []
  const name = response?.map(resp => resp.name) || []

  const personalityFactors = R.filter(
    R.propEq('parentId', TAB_NAMES.PERSONALITY),
    getFactors(),
  )

  const gmaFactor = R.prop(
    'factor',
    R.find(R.propEq('id', TAB_NAMES.GMA), getFactors()),
  )

  const hardinessFactor = R.prop(
    'factor',
    R.find(R.propEq('id', TAB_NAMES.HARDINESS), getFactors()),
  )

  if (!response) {
    return null
  }

  const tabs = {
    [TAB_NAMES.INTRODUCTION]: (
      <Introduction
        {...{
          language,
          project,
          setSelectedCompetency,
          selectedCompetency,
          selectedCandidates,
          response,
          scores,
          name,
          candidates,
        }}
      />
    ),
    [TAB_NAMES.COMPETENCY]: (
      <Competency
        {...{
          language,
          scores,
          response,
          onTab,
          name,
          project,
          candidates,
          selectedCompetency,
          setSelectedCompetency,
          setSelectedCandidates,
          selectedCandidates,
        }}
      />
    ),
    [TAB_NAMES.GMA]: (
      <ProcessingSpeed
        factor={gmaFactor}
        key={gmaFactor}
        {...{
          setSelectedCandidates,
          selectedCandidates,
          candidates,
          language,
          response,
          name,
        }}
      />
    ),
    [TAB_NAMES.PERSONALITY]: (
      <PersonalityFactors
        factors={personalityFactors}
        {...{
          language,
          scores,
          response,
          personalityTab,
          selectedCandidates,
          setSelectedCandidates,
          setPersonalityTab,
          candidates,
          name,
        }}
      />
    ),
    [TAB_NAMES.HARDINESS]: (
      <Factor
        key={hardinessFactor}
        factor={hardinessFactor}
        wrap={false}
        {...{
          language,
          scores,
        }}
      />
    ),
  }

  return tabs[tab]
}
